import { Injectable } from '@angular/core';
import { ITextFinding } from '@interfaces/analytics-details-utlilities.interface';
import { IFinding, IKeywordAnalytics } from '@interfaces/deliverable-analytics.interface';
import { IFindingKeywordMapping, IFindingVisualizer } from '@interfaces/finding-visualizer.interface';
import { AnalyticsDetailsUtilitiesService } from './analytics-details-utilities.service';

@Injectable({
  providedIn: 'root',
})
export class FindingVisualizerService {
  /**
   * Groups keyword findings by their deduplication key (text/audio + timestamp + user).
   *
   * When the backend returns multiple findings for the same sentence — each from a different
   * keyword match — they are consolidated into a single `IFindingVisualizer` entry.
   * The resulting visualizer holds all original findings and the full set of keywords
   * that matched within that sentence.
   *
   * Steps:
   * 1. For each finding across all keywords, compute a deduplication key.
   * 2. Create a new FindingVisualizer if none exists for that key, or add to the existing one.
   * 3. Track which keyword (with its keywordId) is associated via `finding.parentId === keyword.keywordId`.
   */
  public buildFromKeywords<T extends IFinding & ITextFinding>(
    keywords: IKeywordAnalytics<T>[],
    removedIds: ReadonlySet<string> = new Set(),
  ): IFindingVisualizer<T>[] {
    const keywordIdToKeyword = new Map<string, string>();

    for (const kw of keywords) {
      keywordIdToKeyword.set(kw.keywordId, kw.keyword);
    }

    const visualizerMap = new Map<string, IFindingVisualizer<T>>();

    for (const keyword of keywords) {
      for (const finding of keyword.findings ?? []) {
        if (removedIds.has(finding.id)) {
          continue;
        }

        const key = AnalyticsDetailsUtilitiesService.getTextFindingKey(finding);

        if (!visualizerMap.has(key)) {
          visualizerMap.set(key, {
            findings: [],
            id: FindingVisualizerService.toCssId(key),
            keywordMappings: [],
            keywords: [],
          });
        }

        const visualizer = visualizerMap.get(key)!;
        visualizer.findings.push(finding);

        const kwStr = keywordIdToKeyword.get(finding.parentId);

        if (kwStr && !visualizer.keywordMappings.some((m) => m.keywordId === finding.parentId)) {
          const mapping: IFindingKeywordMapping = { keyword: kwStr, keywordId: finding.parentId };
          visualizer.keywordMappings.push(mapping);
          visualizer.keywords.push(kwStr);
        }
      }
    }

    return Array.from(visualizerMap.values()).filter((v) => v.findings.length > 0);
  }

  /**
   * Builds a keyword → row-ID map from an ordered array of visualizers.
   *
   * The order of IDs in each keyword's list matches the order of the visualizers,
   * enabling sequential "jump to next finding" navigation in the UI.
   * When deleting a FindingVisualizer, the `keywordMappings` on each visualizer
   * provide the associated finding IDs needed to remove individual keyword findings.
   */
  public buildKeywordRowIdMap<T extends IFinding>(
    sortedVisualizers: IFindingVisualizer<T>[],
  ): Record<string, string[]> {
    const keywordRowIdMap: Record<string, string[]> = Object.create(null) as Record<string, string[]>;
    const seenPerKeyword = new Map<string, Set<string>>();

    for (const visualizer of sortedVisualizers) {
      for (const { keyword } of visualizer.keywordMappings) {
        if (!keywordRowIdMap[keyword]) {
          keywordRowIdMap[keyword] = [];
          seenPerKeyword.set(keyword, new Set());
        }

        const seen = seenPerKeyword.get(keyword)!;

        if (!seen.has(visualizer.id)) {
          seen.add(visualizer.id);
          keywordRowIdMap[keyword].push(visualizer.id);
        }
      }
    }

    return keywordRowIdMap;
  }

  /**
   * Converts an arbitrary string key into a deterministic, CSS-safe id.
   * Uses FNV-1a 32-bit so the output is always a valid identifier
   * (letter prefix + lowercase hex) with no spaces or special characters.
   */
  private static toCssId(key: string): string {
    let hash = 0x811c9dc5;

    for (let i = 0; i < key.length; i++) {
      hash ^= key.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }

    return `r${hash.toString(16)}`;
  }
}
